import { type ReactElement, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { useAuthStore, useIdentityAvailabilityQuery } from '@/entities/user';
import { routeForStatus, SignUpForm } from '@/features/auth';
import { GuestQuoteOrderHint } from '@/features/guest-quote-claim';
import { AuthPageShell } from '@/widgets/auth-page-shell';

/** 회원가입(/sign-up) — 로그인과 같은 전체 화면 인증 틀 위에 가입 폼과 로그인 링크. */
export const SignUpPage = (): ReactElement => {
  const navigate = useNavigate();
  // 본인인증 단계는 서버가 쓴다고 확인됐을 때만 안내한다(불러오는 중·실패면 이메일 인증만).
  const identityRequired = useIdentityAvailabilityQuery().data?.identityRequired === true;
  // 이미 로그인한 채로 들어오면 가입 폼 대신 상태에 맞는 곳으로 보낸다(마운트 때만 본다 — 가입 성공 이동은 handleSuccess).
  const [redirectTo] = useState<string | null>(() => {
    const { isAuthenticated, user } = useAuthStore.getState();
    if (!isAuthenticated) return null;
    return user ? routeForStatus(user.status) : '/';
  });

  const handleSuccess = (): void => {
    // 가입 직후 상태는 PENDING_EMAIL — 온보딩(이메일 인증 안내)으로 보낸다.
    const status = useAuthStore.getState().user?.status ?? 'PENDING_EMAIL';
    void navigate(routeForStatus(status), { replace: true });
  };

  if (redirectTo !== null) return <Navigate to={redirectTo} replace />;

  return (
    <AuthPageShell
      title="Sign up"
      screenTitle="회원가입"
      description={
        identityRequired
          ? '이메일 인증과 본인인증 후 이용할 수 있습니다.'
          : '이메일 인증 후 이용할 수 있습니다.'
      }
      brandHeadline={
        <>
          디자인을 저장하고,
          <br />
          확정견적을 받아보세요.
        </>
      }
      brandDescription="가입하면 만든 디자인을 저장하고 견적 진행 상황을 한 곳에서 확인할 수 있습니다."
    >
      {/* 링크 화면 [주문하기]로 왔으면 가입·인증 뒤 견적이 연결된다고 알린다(결정 G11). 연결은 ACTIVE 가 되면 앱 셸이 한다. */}
      <GuestQuoteOrderHint />
      <SignUpForm onSuccess={handleSuccess} />

      <p className="mt-6 text-center text-[13px] text-muted-foreground">
        이미 계정이 있으신가요?{' '}
        <Link
          to="/sign-in"
          className="font-semibold text-foreground underline-offset-4 hover:underline"
        >
          로그인
        </Link>
      </p>
    </AuthPageShell>
  );
};
